import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { roles, formatCompensation, type Role } from '@/components/careers/rolesData';
import { RoleDialog } from '@/components/careers/RoleDialog';
import { outlineOnDarkClass } from '@/lib/ctaStyles';

const VALUES = [
  {
    title: 'Ship where it matters',
    description:
      'We embed with customers, take the biggest problem on the table, and put working software in front of them fast.',
  },
  {
    title: 'Determinism over demos',
    description:
      'Agents that impress a room and fail in production are worse than no agents. We measure, replay, and prove reliability.',
  },
  {
    title: 'Small teams, wide surface',
    description:
      'Founding engineers own products, not tickets. If you want scope, you get it — and the responsibility that comes with it.',
  },
  {
    title: 'Honest defaults',
    description:
      'Clear priorities, direct feedback, and calm postmortems. No performative urgency, no political overhead.',
  },
];

const BENEFITS = [
  {
    title: 'Meaningful early equity',
    description: 'Real ownership in a category-defining company.',
  },
  { title: 'Competitive comp', description: 'Bands calibrated against top-tier enterprise SaaS.' },
  {
    title: 'NYC hybrid',
    description:
      'Three days a week together in our New York office, two remote — collaboration when it counts, focus time when it does not.',
  },
  {
    title: 'Home-office stipend',
    description: 'Everything you need to build a durable, comfortable setup.',
  },
  {
    title: 'Health & wellness',
    description: 'Premium medical, dental, and vision for you and your family.',
  },
  { title: 'Learning budget', description: 'Books, courses, conferences — invest in your craft.' },
  { title: 'Generous PTO', description: 'Take the time you need. We track outcomes, not hours.' },
  {
    title: 'Onsite gatherings',
    description: 'Quarterly in-person weeks to align, ship, and connect.',
  },
];

export function CareersPage() {
  const [openRole, setOpenRole] = useState<Role | null>(null);
  const [openGeneral, setOpenGeneral] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<string>('All');

  const departments = useMemo(() => {
    const set = new Set(roles.map((r) => r.department));
    return ['All', ...Array.from(set)];
  }, []);

  const filteredRoles = useMemo(
    () =>
      departmentFilter === 'All' ? roles : roles.filter((r) => r.department === departmentFilter),
    [departmentFilter]
  );

  const dialogOpen = openRole !== null || openGeneral;
  const handleClose = () => {
    setOpenRole(null);
    setOpenGeneral(false);
  };

  return (
    <>
      <Helmet>
        <title>Careers - CriticalBridge.AI</title>
        <meta
          name="description"
          content="Join CriticalBridge.AI and help build the operating layer for enterprise agentic automation. Explore open roles across engineering, AI systems, field engineering, and go-to-market."
        />
        <meta
          name="keywords"
          content="criticalbridge careers, ai jobs, workflow automation jobs, founding engineer, enterprise ai jobs"
        />
        <meta property="og:title" content="Careers - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="Help build the operating layer for enterprise agentic automation."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/careers" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Company / Careers
          </div>
          <h1 className="mt-4 max-w-[26ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Build the operating layer for enterprise agents.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            We're a small, senior team turning agentic automation into infrastructure the Fortune
            1000 will run on. If you want unusual scope, unusual pace, and unusually good teammates
            — read on.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <a href="#open-roles">See open roles</a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className={outlineOnDarkClass}
              onClick={() => setOpenGeneral(true)}
            >
              Send an open application
            </Button>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            How we work
          </span>
          <h2 className="mt-2 text-3xl font-bold">Four principles you'll feel from day one.</h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-lg border bg-card p-5">
                <h3 className="text-lg font-bold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section id="open-roles" className="scroll-mt-20 border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            {roles.length} open {roles.length === 1 ? 'role' : 'roles'}
          </span>
          <h2 className="mt-2 text-3xl font-bold">Open positions</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Pick a role to see the full description and apply. We read every application.
          </p>

          {departments.length > 2 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {departments.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDepartmentFilter(d)}
                  className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                    departmentFilter === d
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 grid gap-3">
            {filteredRoles.map((role) => {
              const comp = formatCompensation(role.compensation);
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setOpenRole(role)}
                  className="flex w-full flex-wrap items-center justify-between gap-4 rounded-lg border bg-background p-5 text-left transition-colors hover:border-primary/40"
                >
                  <div className="min-w-[260px] flex-1">
                    <div className="mb-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-[11px] tracking-wide text-foreground">
                        {role.department}
                      </span>
                      {comp && (
                        <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-[11px] tracking-wide text-foreground">
                          {comp}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold">{role.title}</h3>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>{role.location}</span>
                      <span>{role.employmentType}</span>
                      {role.remote && <span>Remote-friendly</span>}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-sm font-bold text-primary">
                    View role →
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-dashed bg-background p-5">
            <div>
              <h3 className="text-lg font-bold">Don't see your role?</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                We hire against needs, not org charts. Tell us what you'd build.
              </p>
            </div>
            <Button variant="outline" onClick={() => setOpenGeneral(true)}>
              Send an open application
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Benefits
          </span>
          <h2 className="mt-2 text-3xl font-bold">
            Compensation and support that respects your time and your craft.
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-lg border bg-card p-4">
                <h3 className="text-sm font-bold">{b.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-muted py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold">Ready to build?</h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Pick a role or tell us what you'd build. Either way, we'll read it.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" asChild>
              <a href="#open-roles">See open roles</a>
            </Button>
            <Button size="lg" variant="outline" onClick={() => setOpenGeneral(true)}>
              Open application
            </Button>
          </div>
        </div>
      </section>

      <RoleDialog role={openRole} open={dialogOpen} onClose={handleClose} />
    </>
  );
}
